import type { BillingEntitlements } from '@/api/billingApi'
import { t } from '@store/I18nStore'
import { timeUnitWord } from '@utils/dateTime'

export type SubscriptionNoticeCode = 'T7' | 'T3' | 'T0' | string
export type SubscriptionNoticeLevel = 'warning' | 'critical'

export type SubscriptionNotice = {
  code: SubscriptionNoticeCode
  level: SubscriptionNoticeLevel
  expiresAt: string | null
  dismissible: boolean
}

const DAY_MS = 24 * 60 * 60 * 1000
const HIDE_MS = DAY_MS
const NOTICE_HIDE_PREFIX = 'billing.noticeHidden:'
const EXPIRED_PROMPT_KEY = 'billing.expiredPromptSeen'

const MONTHS_RU = [
  t('time.months.january'),
  t('time.months.february'),
  t('time.months.march'),
  t('time.months.april'),
  t('time.months.may'),
  t('time.months.june'),
  t('time.months.july'),
  t('time.months.august'),
  t('time.months.september'),
  t('time.months.october'),
  t('time.months.november'),
  t('time.months.december'),
]

export function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function normalizeNoticeCode(raw: unknown): SubscriptionNoticeCode {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
  if (s === 'T7' || s === 'T07' || s === '7') return 'T7'
  if (s === 'T3' || s === 'T03' || s === '3') return 'T3'
  if (s === 'T0' || s === 'T00' || s === '0') return 'T0'
  return s || 'T3'
}

export function normalizeNoticeLevel(
  raw: unknown,
  code?: SubscriptionNoticeCode,
): SubscriptionNoticeLevel {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
  if (s === 'critical' || s === 'error' || s === 'danger') return 'critical'
  if (s === 'warning' || s === 'warn') return 'warning'
  return code === 'T0' ? 'critical' : 'warning'
}

export function daysUntil(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null
  const end = new Date(iso).getTime()
  if (!Number.isFinite(end)) return null
  return Math.ceil((end - now) / DAY_MS)
}

export function ruDayWord(days: number): string {
  return timeUnitWord('day', days)
}

export function formatNoticeDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getDate()} ${MONTHS_RU[date.getMonth()]}`
}

export function formatNoticeTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

export function formatNoticeDateTime(iso: string | null | undefined): string {
  const date = formatNoticeDate(iso)
  const time = formatNoticeTime(iso)
  if (date && time) return date + ' ' + t('time.at_time', { time })
  return date || time
}

export function subscriptionExpiryIso(entitlements: BillingEntitlements | null | undefined): string | null {
  const sub = entitlements?.subscription
  return sub?.currentPeriodEnd || sub?.accessUntil || sub?.trialEndsAt || null
}

export function deriveExpiringNotice(
  entitlements: BillingEntitlements | null | undefined,
  now = Date.now(),
): SubscriptionNotice | null {
  const expiresAt = subscriptionExpiryIso(entitlements)
  const days = daysUntil(expiresAt, now)
  if (days == null) return null
  if (days <= 0) {
    return { code: 'T0', level: 'critical', expiresAt, dismissible: false }
  }
  if (days <= 3) {
    return { code: 'T3', level: 'warning', expiresAt, dismissible: true }
  }
  // T7 (7 days) is unused — first reminder is T3 for trial and paid plans.
  return null
}

/** Backend may still emit T7; the panel only reminds at T3 / T0. */
export function isNoticeSuppressedForEntitlements(
  notice: SubscriptionNotice,
  _entitlements?: BillingEntitlements | null,
): boolean {
  return notice.code === 'T7'
}

export function parseSubscriptionNotice(raw: unknown): SubscriptionNotice | null {
  const root = asRecord(raw)
  if (!root) return null
  const nested = asRecord(root.data) ?? asRecord(root.notice) ?? root
  const code = normalizeNoticeCode(nested.code ?? nested.triggerCode ?? nested.trigger_code ?? root.code)
  const expiresAt =
    (typeof nested.expiresAt === 'string' && nested.expiresAt) ||
    (typeof nested.expires_at === 'string' && nested.expires_at) ||
    (typeof nested.expiredAt === 'string' && nested.expiredAt) ||
    (typeof nested.expired_at === 'string' && nested.expired_at) ||
    (typeof root.expiresAt === 'string' && root.expiresAt) ||
    (typeof root.expires_at === 'string' && root.expires_at) ||
    (typeof root.expiredAt === 'string' && root.expiredAt) ||
    (typeof root.expired_at === 'string' && root.expired_at) ||
    null
  const level = normalizeNoticeLevel(nested.level ?? root.level, code)
  return {
    code,
    level,
    expiresAt,
    dismissible: level !== 'critical' && nested.dismissible !== false,
  }
}

export function parseBillingWsType(raw: unknown): string {
  const root = asRecord(raw)
  if (!root) return ''
  const nested = asRecord(root.data)
  return String(root.type ?? root.event ?? nested?.type ?? nested?.event ?? '')
    .trim()
    .toLowerCase()
}

export function isNoticeHidden(code: SubscriptionNoticeCode): boolean {
  if (!code || code === 'T0') return false
  try {
    const raw = localStorage.getItem(`${NOTICE_HIDE_PREFIX}${code}`)
    const until = raw ? Number(raw) : 0
    return Number.isFinite(until) && until > Date.now()
  } catch {
    return false
  }
}

export function hideNoticeForADay(code: SubscriptionNoticeCode): void {
  if (!code || code === 'T0') return
  try {
    localStorage.setItem(`${NOTICE_HIDE_PREFIX}${code}`, String(Date.now() + HIDE_MS))
  } catch {
    /* ignore */
  }
}

export function clearHiddenNotices(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(NOTICE_HIDE_PREFIX)) localStorage.removeItem(key)
    }
    sessionStorage.removeItem(EXPIRED_PROMPT_KEY)
  } catch {
    /* ignore */
  }
}

export function hasSeenExpiredPromptThisSession(): boolean {
  try {
    return sessionStorage.getItem(EXPIRED_PROMPT_KEY) === '1'
  } catch {
    return false
  }
}

export function markExpiredPromptSeen(): void {
  try {
    sessionStorage.setItem(EXPIRED_PROMPT_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function mergeNotices(
  current: SubscriptionNotice | null,
  incoming: SubscriptionNotice,
): SubscriptionNotice {
  if (!current) return incoming
  if (incoming.level === 'critical' || current.level !== 'critical') {
    if (current.code === 'T3' && incoming.code === 'T7' && incoming.level !== 'critical') {
      return { ...current, expiresAt: incoming.expiresAt || current.expiresAt }
    }
    return incoming
  }
  return current
}
